import { isRosbridgeActionFeedbackMessage, isRosbridgeStatusMessage, Ros, RosbridgeActionFeedbackMessage, RosbridgeMessage, Action as RoslibAction } from "roslib";
import { ROSLibJSControlSource } from "../control_source/ROSLibJS";
import { RosLibJsServiceCaller } from "../services/sources/roslibjs";
import { Subscriber } from "../control_source/base";

export class ActionInProgressError extends Error {
    constructor(actionName: string, goalId: string) {
        super(`Action ${actionName} already has goal ${goalId} in progress`);
        this.name = "ActionInProgressError";
    }
}

export enum GoalStatusCode {
    STATUS_UNKNOWN = 0,
    STATUS_ACCEPTED = 1,
    STATUS_EXECUTING = 2,
    STATUS_CANCELING = 3,
    STATUS_SUCCEEDED = 4,
    STATUS_CANCELED = 5,
    STATUS_ABORTED = 6,
}

interface R2C2RosbridgeActionFeedbackMessage extends RosbridgeActionFeedbackMessage {
    goal_id: string;
}

interface SendActionGoalResponseMessage {
    op: "send_action_goal_response";
    action: string;
    goal_id: string;
    accepted: boolean;
    id?: string;
}

// Type guard for send_action_goal_response messages
function isSendActionGoalResponseMessage(msg: any): msg is SendActionGoalResponseMessage {
    return msg.op === "send_action_goal_response";
}

interface GoalStatus {
    goal_info: {
        goal_id: { id: string; stamp: any };
        stamp: { sec: number, nanosec: number };
    };
    status: GoalStatusCode;
    text?: string;
}

interface GoalStatusArray {
    header: any;
    status_list: Array<GoalStatus>;
}

export interface ActionFeedback<TFeedback> {
    goal_id: { id: string; stamp: any };
    feedback: TFeedback;
}

interface GetResultRequest {
    goal_id: { id: string; stamp: any };
}

export interface GetResultResponse<TResult> {
    status: GoalStatusCode;
    result: TResult;
}

export type StatusCallback = (statuses: Array<GoalStatus>) => void;
export type FeedbackCallback<TFeedback> = (feedback: ActionFeedback<TFeedback>) => void;
export type ResultCallback<TResult> = (result: GetResultResponse<TResult>) => void;
export type FailedCallback = (error: string) => void;

// Convert UUID string to uint8[16] array for ROS2 action services
// Example: "282caf42-d0e5-4477-a503-471100de8c26" -> [40, 44, 175, 66, ...]
const uuidStringToBytes = (uuidStr: string): number[] => {
    // Remove dashes from UUID string
    const hex = uuidStr.replace(/-/g, '');

    // Convert hex string to bytes
    const bytes: number[] = [];
    for (let i = 0; i < hex.length; i += 2) {
        bytes.push(parseInt(hex.substr(i, 2), 16));
    }

    return bytes;
}

// Convert base64-encoded UUID bytes to standard UUID string format
// Example: base64 string -> "282caf42-d0e5-4477-a503-471100de8c26"
const base64ToUuid = (base64Str: string): string => {
    // Decode base64 to binary string
    const binaryStr = atob(base64Str);

    // Convert binary string to hex string
    let hex = '';
    for (let i = 0; i < binaryStr.length; i++) {
        const byte = binaryStr.charCodeAt(i).toString(16).padStart(2, '0');
        hex += byte;
    }

    // Insert dashes at UUID positions: 8-4-4-4-12
    return `${hex.substr(0, 8)}-${hex.substr(8, 4)}-${hex.substr(12, 4)}-${hex.substr(16, 4)}-${hex.substr(20, 12)}`;
}

export class Action<TGoal = unknown, TFeedback = unknown, TResult = unknown> {
    private actionName: string;
    private actionType: string | null = null;
    private ros: Ros;
    private roslibAction!: RoslibAction<TGoal, TFeedback, TResult>;
    private controlSource: ROSLibJSControlSource;
    private serviceCaller: RosLibJsServiceCaller;
    private goalId: string | null = null;
    private statusHandler: Subscriber | null = null;
    private feedbackHandler: Subscriber | null = null;
    private onStatusCallback: StatusCallback | null = null;
    private onFeedbackCallback: FeedbackCallback<TFeedback> | null = null;
    private onResultCallback: ResultCallback<TResult> | null = null;
    private onFailedCallback: FailedCallback | null = null;
    private processingTerminal = false;
    private initPromise: Promise<void>;
    private static isRosbridgePatched = false;

    constructor(ros: Ros, actionName: string) {
        this.actionName = actionName;
        this.ros = ros;

        // Patch roslibjs handleMessage to support send_action_goal_response (only once)
        if (!Action.isRosbridgePatched) {
            this.patchRosbridgeHandleMessage();
            Action.isRosbridgePatched = true;
        }

        // Create control source using the existing ros connection
        this.controlSource = ROSLibJSControlSource.fromSocket(ros);
        console.log('action: the controlSource', this.controlSource)
        this.serviceCaller = new RosLibJsServiceCaller({ ros });

        // Discover action type and initialize RoslibAction
        this.initPromise = this.discoverActionType();
    }

    /**
     * Monkey-patch roslibjs's handleMessage to support send_action_goal_response messages.
     * This is necessary because roslibjs doesn't natively handle this custom message type.
     */
    private patchRosbridgeHandleMessage(): void {
        const rosAny = this.ros as any;
        const originalHandleMessage = rosAny.handleMessage.bind(this.ros);

        rosAny.handleMessage = (message: any) => {
            // Check if this is a send_action_goal_response message
            if (isSendActionGoalResponseMessage(message)) {
                console.log('[Action] Intercepted send_action_goal_response:', message);
                // Emit on message.id (like other action messages)
                if (message.id) {
                    (this.ros as any).emit(message.id, message);
                }
                return; // Don't pass to original handler
            }

            // For all other messages, use original roslibjs handling
            originalHandleMessage(message);
        };

        // console.log('[Action] Patched roslibjs handleMessage to support send_action_goal_response');
    }

    private async discoverActionType(): Promise<void> {
        // Query the feedback topic type to discover the action type
        const feedbackTopic = `${this.actionName}/_action/feedback`;

        return new Promise((resolve, reject) => {
            this.ros.getTopicType(
                feedbackTopic,
                (feedbackType: string) => {
                    console.log(`[Action ${this.actionName}] Discovered feedback type:`, feedbackType);

                    // Parse action type from feedback type
                    // Example: "r2c2_actions_interfaces/action/NavigateStairs_FeedbackMessage"
                    // -> "r2c2_actions_interfaces/NavigateStairs"
                    const match = feedbackType.match(/^(.+)\/action\/(.+)_FeedbackMessage$/);
                    if (match) {
                        this.actionType = `${match[1]}/${match[2]}`;
                    } else {
                        // Fallback: try removing _FeedbackMessage suffix
                        this.actionType = feedbackType.replace(/_FeedbackMessage$/, '');
                    }

                    console.log(`[Action ${this.actionName}] Derived action type:`, this.actionType);

                    // Create RoslibAction for sending goals
                    // We pass no-op callbacks to sendGoal and handle status/feedback/result via our own subscriptions
                    this.roslibAction = new RoslibAction<TGoal, TFeedback, TResult>({
                        ros: this.ros,
                        name: this.actionName,
                        actionType: this.actionType,
                    });

                    resolve();
                },
                (error: string) => {
                    console.error(`[Action ${this.actionName}] Failed to discover action type:`, error);
                    reject(new Error(`Failed to discover action type for ${this.actionName}: ${error}`));
                }
            );
        });
    }

    private async ensureInitialized(): Promise<void> {
        await this.initPromise;
    }

    async listen({
        onStatus,
        onFeedback,
        onResult,
        onFailed
    }: {
        onStatus?: StatusCallback,
        onFeedback?: FeedbackCallback<TFeedback>,
        onResult?: ResultCallback<TResult>,
        onFailed?: FailedCallback,
    } = {}): Promise<void> {
        await this.ensureInitialized();
        this.stopListen()

        // Store callbacks if provided, otherwise keep existing ones
        if (onStatus) this.onStatusCallback = onStatus;
        if (onFeedback) this.onFeedbackCallback = onFeedback;
        if (onResult) this.onResultCallback = onResult;
        if (onFailed) this.onFailedCallback = onFailed;

        // Subscribe to status topic
        const statusTopic = `${this.actionName}/_action/status`;
        this.statusHandler = (data: GoalStatusArray) => {
            // console.log(`[Action ${this.actionName}] Status message:`, data);

            if (this.onStatusCallback) {
                console.log('got this.onStatusCallback. invoking with', data.status_list)
                this.onStatusCallback(data.status_list);
            }

            // Terminal state detection for active goal
            if (this.goalId && !this.processingTerminal) {
                const ourGoal = data.status_list.find(
                    (s: GoalStatus) => s.goal_info.goal_id.id === this.goalId
                );
                if (ourGoal) {
                    const terminalStates = [
                        GoalStatusCode.STATUS_SUCCEEDED,
                        GoalStatusCode.STATUS_ABORTED,
                        GoalStatusCode.STATUS_CANCELED,
                    ];
                    if (terminalStates.includes(ourGoal.status)) {
                        this.processingTerminal = true;

                        if (ourGoal.status === GoalStatusCode.STATUS_SUCCEEDED) {
                            this.getResult().then(response => {
                                this.goalId = null;
                                this.processingTerminal = false;
                                if (this.onResultCallback) this.onResultCallback(response);
                                this.stopListen();
                            }).catch((err: unknown) => {
                                this.goalId = null;
                                this.processingTerminal = false;
                                const msg = err instanceof Error ? err.message : String(err);
                                if (this.onFailedCallback) this.onFailedCallback(`Failed to get result: ${msg}`);
                                this.stopListen();
                            });
                        } else {
                            this.goalId = null;
                            this.processingTerminal = false;
                            if (ourGoal.status === GoalStatusCode.STATUS_ABORTED) {
                                if (this.onFailedCallback) this.onFailedCallback('aborted');
                            } else if (ourGoal.status === GoalStatusCode.STATUS_CANCELED) {
                                if (this.onFailedCallback) this.onFailedCallback('canceled');
                            }
                            this.stopListen();
                        }
                    }
                }
            }
        };

        await this.controlSource.subscribe(statusTopic, this.statusHandler);

        // Subscribe to feedback topic
        const feedbackTopic = `${this.actionName}/_action/feedback`;
        this.feedbackHandler = (data: ActionFeedback<TFeedback>) => {
            // console.log(`[Action ${this.actionName}] Feedback message:`, data);

            if (this.onFeedbackCallback) {
                this.onFeedbackCallback(data);
            }
        };

        await this.controlSource.subscribe(feedbackTopic, this.feedbackHandler);
    }

    /**
     * Internal method to send action goal with proper listener setup.
     * Mimics roslibjs behavior but handles send_action_goal_response.
     */
    private sendGoalInternal(goal: TGoal): Promise<string> {
        return new Promise((resolve, reject) => {
            // Generate composite action goal ID (like roslibjs does)
            const actionGoalId = `send_action_goal:${this.actionName}:${crypto.randomUUID()}`;

            console.log(`[Action ${this.actionName}] Generated actionGoalId:`, actionGoalId);

            // Register listener BEFORE sending goal (prevents race condition)
            const messageListener = (message: any) => {
                console.log(`[Action ${this.actionName}] Received message on ${actionGoalId}:`, message);

                if (isSendActionGoalResponseMessage(message)) {
                    const goalIdB64 = message.goal_id;
                    const goalId = base64ToUuid(goalIdB64);
                    console.log('goalId base64:', goalIdB64);
                    console.log('goalId:', goalId);
                    console.log('accepted:', message.accepted);

                    this.ros.removeListener(actionGoalId, messageListener);

                    if (!message.accepted) {
                        reject(new Error(`Goal was rejected by action server ${this.actionName}`));
                        return;
                    }

                    this.goalId = goalId;
                    resolve(goalId);
                } else if (isRosbridgeActionFeedbackMessage(message)) {
                    // Handle feedback via our own callback
                    if (this.onFeedbackCallback) {
                        this.onFeedbackCallback((message as any).values);
                    }
                }
                // Note: We don't handle action_result here, use our own subscriptions for that
            };

            this.ros.on(actionGoalId, messageListener);

            // Send the goal to rosbridge
            (this.ros as any).callOnConnection({
                op: "send_action_goal",
                id: actionGoalId,
                action: this.actionName,
                action_type: this.actionType,
                args: goal,
                feedback: true,
            });

            console.log(`[Action ${this.actionName}] Sent goal with ID ${actionGoalId}`);
        });
    }

    async sendGoal(goal: TGoal): Promise<string> {
        await this.ensureInitialized();

        if (this.goalId !== null) {
            throw new ActionInProgressError(this.actionName, this.goalId);
        }

        console.log(`[Action ${this.actionName}] Sending goal:`, goal);

        // const listenOpts: Parameters<typeof this.listen>[0] = {}
        // if (this.onStatusCallback) listenOpts.onStatus = this.onStatusCallback
        // await this.listen(listenOpts)
        await this.listen()

        return this.sendGoalInternal(goal);
    }

    // Old method using roslibAction.cancelGoal()
    // cancelGoal(goalId?: string): void {
    //     const targetGoalId = goalId || this.goalId;
    //     if (!targetGoalId) {
    //         throw new Error("No goal ID to cancel");
    //     }
    //
    //     console.log(`[Action ${this.actionName}] Canceling goal:`, targetGoalId);
    //     this.roslibAction.cancelGoal(targetGoalId);
    // }

    async cancelGoal(goalId?: string): Promise<void> {
        const targetGoalId = goalId || this.goalId;
        if (!targetGoalId) {
            throw new Error("No goal ID to cancel");
        }

        console.log(`[Action ${this.actionName}] Canceling goal:`, targetGoalId);

        // Convert UUID string to byte array
        const uuidBytes = uuidStringToBytes(targetGoalId);

        // Construct CancelGoal service request
        // https://docs.ros.org/en/humble/p/action_msgs/srv/CancelGoal.html
        const request = {
            goal_info: {
                goal_id: {
                    uuid: uuidBytes
                },
                stamp: {
                    sec: Math.floor(Date.now() / 1000),
                    nanosec: 0,
                }
                // stamp is optional, will use default
            }
        };

        const cancelGoalService = `${this.actionName}/_action/cancel_goal`;

        try {
            console.log('calling cancelGoalService at ', cancelGoalService)
            const response = await this.serviceCaller.callService<any, any>(
                cancelGoalService,
                request,
                { serviceType: 'action_msgs/srv/CancelGoal' }
            );
            console.log(`[Action ${this.actionName}] Cancel goal response:`, response);
        } catch (error) {
            console.error(`[Action ${this.actionName}] Failed to cancel goal:`, error);
            throw error;
        }
    }

    stopListen(): void {
        if (this.statusHandler) {
            const statusTopic = `${this.actionName}/_action/status`;
            this.controlSource.unsubscribe(statusTopic, this.statusHandler);
            this.statusHandler = null;
            console.log('stopped listening to ', statusTopic)
        }

        if (this.feedbackHandler) {
            const feedbackTopic = `${this.actionName}/_action/feedback`;
            this.controlSource.unsubscribe(feedbackTopic, this.feedbackHandler);
            this.feedbackHandler = null;
            console.log('stopped listening to ', feedbackTopic)
        }

        // Don't clear callbacks - they persist across listen()/stopListen() cycles
        this.processingTerminal = false;
    }

    private async getResult(): Promise<GetResultResponse<TResult>> {
        if (!this.goalId) {
            throw new Error("No active goal to get result for");
        }

        const request: GetResultRequest = {
            goal_id: {
                id: this.goalId,
                stamp: { sec: 0, nanosec: 0 },
            },
        };

        const getResultService = `${this.actionName}/_action/get_result`;

        return this.serviceCaller.callService<
            GetResultRequest,
            GetResultResponse<TResult>
        >(getResultService, request);
    }

    // Getter for current goal ID (useful for debugging)
    getCurrentGoalId(): string | null {
        return this.goalId;
    }
}
